import { useCallback, useEffect, useRef, useState } from 'react';

/** Ruhezeit nach dem letzten Klick, bevor die gesammelte Lebensänderung an den Server geht. */
export const LIFE_COMMIT_DELAY_MS = 2000;

/**
 * Lebens-Sammler (Spec 2026-10-21 §5.6): Klicks auf Plus/Minus laufen auf, die Zahl ändert sich
 * sofort, und nach `LIFE_COMMIT_DELAY_MS` ohne weiteren Klick geht die Summe als EINE Änderung
 * per `onCommit(delta)` raus.
 *
 * - `displayedLife = serverLife + pendingDelta`: ein Serverstand, der während der Sammlung
 *   eintrifft, verschiebt nur die Basis; die Zahl springt nicht zurück.
 * - `add(delta)` setzt die Ruhezeit neu; `flushNow()` übergibt sofort. Unmount übergibt ebenfalls.
 * - `connected === false`: Die Ruhezeit übergibt nichts, weil der Socket-Hook ohne Verbindung
 *   jede Nachricht verwirft. Die Summe bleibt stehen und geht beim Wiederverbinden sofort raus —
 *   der Nutzer hat seine Klicks gesehen. Verlässt er das Brett, solange die Verbindung fehlt,
 *   übergibt der Unmount die Summe trotzdem, und der Socket-Hook verwirft sie: das ist der
 *   einzige Weg, auf dem gesammelte Klicks verloren gehen.
 */
export function useLifeDelta({ serverLife, onCommit, connected = true }) {
  const [pendingDelta, setPendingDelta] = useState(0);
  const pendingRef = useRef(0);
  const timerRef = useRef(null);
  const onCommitRef = useRef(onCommit);
  const connectedRef = useRef(connected);

  useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const flushNow = useCallback(() => {
    clearTimer();
    const delta = pendingRef.current;
    if (delta === 0) return;
    pendingRef.current = 0;
    setPendingDelta(0);
    onCommitRef.current?.(delta);
  }, [clearTimer]);

  const add = useCallback((delta) => {
    pendingRef.current += delta;
    setPendingDelta(pendingRef.current);
    clearTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (connectedRef.current) flushNow();
    }, LIFE_COMMIT_DELAY_MS);
  }, [clearTimer, flushNow]);

  useEffect(() => {
    connectedRef.current = connected;
    // Läuft die Ruhezeit noch, übergibt sie selbst; sonst wartet eine Summe auf die Verbindung.
    if (connected && timerRef.current === null) flushNow();
  }, [connected, flushNow]);

  useEffect(() => flushNow, [flushNow]);

  return { displayedLife: serverLife + pendingDelta, pendingDelta, add, flushNow };
}
